import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Youtube, Music2, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function extractSpotifyId(url) {
  if (!url) return null;
  const match = url.match(/spotify\.com\/episode\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
}

function extractYoutubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([^&?/]+)/);
  return match ? match[1] : null;
}

export default function EpisodePage() {
  const { episodeId } = useParams();
  const [episode, setEpisode] = useState(null);
  const [allEpisodes, setAllEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [epRes, allRes] = await Promise.all([
          axios.get(`${API}/episodes/${episodeId}`),
          axios.get(`${API}/episodes`)
        ]);
        setEpisode(epRes.data);
        setAllEpisodes(allRes.data || []);
      } catch (err) {
        console.error('Failed to fetch episode:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [episodeId]);

  if (loading) {
    return (
      <main className="pt-16 md:pt-20 min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[#FF2A2A] font-heading text-2xl">LOADING...</div>
      </main>
    );
  }

  if (!episode) {
    return (
      <main className="pt-16 md:pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl uppercase mb-4">EPISODE NOT FOUND</h1>
          <Link to="/archive" className="text-[#FF2A2A] font-body font-bold hover:underline">Back to Episodes</Link>
        </div>
      </main>
    );
  }

  const spotifyId = extractSpotifyId(episode.spotify_url);
  const youtubeId = extractYoutubeId(episode.youtube_url);

  // Find prev/next episodes
  const currentIdx = allEpisodes.findIndex(e => e.id === episode.id);
  const prevEp = currentIdx < allEpisodes.length - 1 ? allEpisodes[currentIdx + 1] : null;
  const nextEp = currentIdx > 0 ? allEpisodes[currentIdx - 1] : null;

  return (
    <main data-testid="episode-page" className="pt-16 md:pt-20 min-h-screen">
      {/* Back Nav */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-8">
        <Link
          to="/archive"
          data-testid="back-to-archive"
          className="inline-flex items-center gap-2 font-body text-sm text-[#A1A1AA] hover:text-white transition-colors"
        >
          <ArrowLeft size={16} /> Back to Episodes
        </Link>
      </div>

      {/* Episode Header */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#FF2A2A] px-4 py-1.5">
              <span className="font-body text-sm font-bold text-white uppercase tracking-wider">
                EPISODE {episode.episode_number}
              </span>
            </div>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-none mb-6">
            {episode.title}
          </h1>
          <p className="font-body text-base text-[#A1A1AA] max-w-3xl leading-relaxed">
            {episode.description}
          </p>
        </motion.div>
      </section>

      {/* Uploaded Video */}
      {episode.video_url && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 pb-8" data-testid="episode-video-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
          >
            <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A] mb-4">
              WATCH EPISODE
            </p>
            <div className="aspect-video bg-[#141414] rounded-xl border border-white/5 overflow-hidden">
              <video
                data-testid="episode-uploaded-video"
                src={`${process.env.REACT_APP_BACKEND_URL}${episode.video_url}`}
                controls
                className="w-full h-full"
                poster={episode.thumbnail_url || undefined}
              />
            </div>
          </motion.div>
        </section>
      )}

      {/* Spotify Embedded Player */}
      {spotifyId && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 pb-8" data-testid="spotify-embed-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A] mb-4">
              LISTEN NOW
            </p>
            <div className="bg-[#141414] rounded-xl border border-white/5 p-4 overflow-hidden">
              <iframe
                data-testid="spotify-embed-player"
                src={`https://open.spotify.com/embed/episode/${spotifyId}?utm_source=generator&theme=0`}
                width="100%"
                height="232"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title={`${episode.title} - Spotify Player`}
                className="rounded-none"
              />
            </div>
          </motion.div>
        </section>
      )}

      {/* YouTube Embed */}
      {youtubeId && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 pb-8" data-testid="youtube-embed-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A] mb-4">
              WATCH ON YOUTUBE
            </p>
            <div className="aspect-video bg-[#141414] rounded-xl border border-white/5 overflow-hidden">
              <iframe
                data-testid="youtube-embed-player"
                src={`https://www.youtube.com/embed/${youtubeId}`}
                title={episode.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </motion.div>
        </section>
      )}

      {/* External Links */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-8">
        <div className="flex flex-wrap gap-4">
          {episode.youtube_url && (
            <a
              href={episode.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="episode-youtube-external"
              className="inline-flex items-center gap-2 bg-[#FF2A2A] text-white font-body font-bold uppercase tracking-wide px-6 py-3 hover:bg-[#E60000] transition-all hover:-translate-y-1"
            >
              <Youtube size={18} /> Watch on YouTube
            </a>
          )}
          {episode.spotify_url && (
            <a
              href={episode.spotify_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="episode-spotify-external"
              className="inline-flex items-center gap-2 border-2 border-white/20 text-white font-body font-bold uppercase tracking-wide px-6 py-3 hover:border-[#1DB954] hover:text-[#1DB954] transition-all hover:-translate-y-1"
            >
              <Music2 size={18} /> Open in Spotify
            </a>
          )}
        </div>
      </section>

      {/* Prev/Next Navigation */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 border-t border-white/5">
        <div className="flex justify-between items-center">
          {prevEp ? (
            <Link
              to={`/episode/${prevEp.id}`}
              data-testid="prev-episode-link"
              className="group flex items-center gap-3 text-[#A1A1AA] hover:text-white transition-colors"
            >
              <ChevronLeft size={20} className="text-[#FF2A2A]" />
              <div>
                <p className="font-body text-xs text-[#71717A] uppercase tracking-wider">Previous</p>
                <p className="font-body text-sm font-semibold">EP {prevEp.episode_number}: {prevEp.title}</p>
              </div>
            </Link>
          ) : <div />}
          {nextEp ? (
            <Link
              to={`/episode/${nextEp.id}`}
              data-testid="next-episode-link"
              className="group flex items-center gap-3 text-[#A1A1AA] hover:text-white transition-colors text-right"
            >
              <div>
                <p className="font-body text-xs text-[#71717A] uppercase tracking-wider">Next</p>
                <p className="font-body text-sm font-semibold">EP {nextEp.episode_number}: {nextEp.title}</p>
              </div>
              <ChevronRight size={20} className="text-[#FF2A2A]" />
            </Link>
          ) : <div />}
        </div>
      </section>
    </main>
  );
}
